import {LoadState, RunGroupID, RunStatus, RunStatusAPI} from 'enums';
import {IRun, IRunAPI, ITest, ITestAPI, ITestInfo, ITestInfoAPI} from "interfaces";
// Not using index import for services to avoid circular dependency
import {axiosServer} from './API';

export interface IStartRunResponse {
    device_under_test: {
        created_at: string,
        run_id: string,
    }
    run_id: string,
    sse_stream: string,
}

class _TestsService {
    public testsInfoState = LoadState.Initial;

    private testNamesById: {[testId: string]: string} = {};
    private testsInfoPromise: Promise<ITestInfo[]>;

    constructor() {}

    public getTestsInfo(): Promise<ITestInfo[]> {
        if (this.testsInfoState === LoadState.Initial || this.testsInfoState === LoadState.Error) {
            this.testsInfoState = LoadState.Loading;
            
            this.testsInfoPromise = axiosServer.get<ITestInfoAPI[]>('/test-names')
                .then((response) => {
                    this.testsInfoState = LoadState.Success;

                    // Populate our lookup map
                    for (const testInfo of response.data) {
                        this.testNamesById[testInfo.test_id] = testInfo.test_name;
                    }

                    return response.data.map((test) => {
                        return {
                            description: test.test_description,
                            groupName: test.test_group,
                            id: test.test_id,
                            enumKey: test.test_enum_key,
                            name: test.test_name,
                        };
                    })
                })
            this.testsInfoPromise.catch((err) => {
                console.error('Error loading test names: ', err.response);
                this.testsInfoState = LoadState.Error;
            })
        }
        return this.testsInfoPromise;
    }

    public getTestName(testId: number): string {
        if (this.testNamesById[testId]) {
            return this.testNamesById[testId];
        }
        return testId + '';
    }

    public getRun(runId: string): Promise<IRun> {
        const p = axiosServer.get<IRunAPI>('/dut/' + runId).then((response) => {
            const runAPI = response.data;

            // This is almost the same as the api's format, we're just converting the createdAt date and mapping the status.
            return {
                id: runAPI.run_id,
                deviceId: runAPI.device_id,
                location: runAPI.location,
                serialNumber: runAPI.serial_number,
                macAddress: runAPI.mac_address,
                createdAt: new Date(runAPI.created_at),
                testsPassed: runAPI.tests_passed || 0,
                testsFailed: runAPI.tests_failed || 0,
                testsCanceled: runAPI.tests_canceled || 0,
                status: runAPI.status_id === RunStatusAPI.InProgress ? RunStatus.Running : RunStatus.Initial,
                testResults: runAPI.test_results,
            }
        });
        p.catch((err) => {
            console.error('Error fetching run: ', err.response);
        });
        return p;
    }

    public startRun(deviceId: number, runGroupIds: RunGroupID[]): Promise<IStartRunResponse> {
        const p =  axiosServer.post<IStartRunResponse>('/device-tests', {
            device_id: deviceId,
            run_group_ids: runGroupIds,
        }).then((response) => {
            return response.data;
        });
        p.catch((err) => {
            console.error('Error starting run: ', err.response);
        });
        return p;
    }

    public stopRun(runId: string): Promise<any> {
        const p = axiosServer({
            method: 'delete',
            url: '/device-tests/' + runId,
        });
        p.catch((err) => {
            console.error('Error stopping run: ', err.response);
            throw err;
        });
        return p;
    }

    public endMessageValidation(deviceId: number): Promise<any> {
        return axiosServer({
            method: 'get',
            url: '/end-message-validation/' + deviceId,
        })
        .catch((err) => {
            console.error('Error stopping run: ', err.response);
            throw err;
        });
    }
}

export const TestsService = new _TestsService();